/**
 * Session detail. Renders the transcript bubbles (you = clay, guest = sage)
 * with per-turn Original / Spoken playback chips.
 */
import { useEffect, useRef, useState } from "react";
import * as api from "../api";
import { Btn, IconBtn, LangBadge, SpeakerDot, langByCode, useLanguages, type Who } from "../ui";
import { Icon } from "../icons";
import type { Go } from "../App";

export function SessionScreen({ sessionId, go }: { sessionId: number; go: Go }) {
  const [session, setSession] = useState<api.SessionDetailOut | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useLanguages();

  useEffect(() => {
    api.getSession(sessionId).then(setSession).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [sessionId]);

  async function del() {
    if (!confirm("Delete this conversation and its audio? This can't be undone.")) return;
    try {
      await api.deleteSession(sessionId);
      go("history");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  if (err) {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 28px" }}>
        <BackLink go={go} />
        <div style={{ marginTop: 20, color: "var(--bad)" }}>{err}</div>
      </div>
    );
  }
  if (!session) {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 28px", color: "var(--ink-3)" }}>
        <BackLink go={go} />
        <div style={{ marginTop: 24 }}>Loading…</div>
      </div>
    );
  }

  const start = new Date(session.started_at);
  const end = session.ended_at ? new Date(session.ended_at) : null;
  const dur = end ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 1000)) : null;
  const totalTurns = session.utterances.reduce((acc, u) => acc + u.turns.length, 0);

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "28px 28px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <BackLink go={go} />
        <IconBtn name="trash" onClick={del} variant="surface" size={38} ic={18} color="var(--bad)" title="Delete conversation" />
      </div>
      <h1 className="display" style={{
        fontSize: 26, fontWeight: 700, letterSpacing: -0.5, color: "var(--ink)", margin: "20px 0 6px",
      }}>
        {start.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
      </h1>
      <div style={{ fontSize: 13.5, color: "var(--ink-3)" }}>
        {start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
        {dur != null ? ` · ${fmtDur(dur)}` : ""} · {totalTurns} turns
      </div>

      <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 14 }}>
        {session.utterances.flatMap((u) => u.turns.map((t) => (
          <TurnRow key={`${u.id}-${t.id}`} sessionId={session.id} uttId={u.utt_id} turn={t} />
        )))}
        {totalTurns === 0 && <div style={{ color: "var(--ink-3)" }}>No transcript was recorded for this session.</div>}
      </div>
    </div>
  );
}

function TurnRow({ sessionId, uttId, turn }: { sessionId: number; uttId: string; turn: api.TurnOut }) {
  const who: Who = turn.label === "owner" ? "you" : "guest";
  const srcL = turn.src_lang ? langByCode(turn.src_lang) : null;
  const tgtL = turn.tgt_lang ? langByCode(turn.tgt_lang) : null;
  return (
    <div style={{
      background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-lg)",
      padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <SpeakerDot who={who} />
        <span style={{ fontSize: 13, fontWeight: 700, color: `var(--${who}-meta)`, textTransform: "capitalize" }}>
          {who === "you" ? "You" : "Guest"}
        </span>
        {srcL && <LangBadge short={srcL.short} who={who} size="sm" />}
        {tgtL && srcL && tgtL.code !== srcL.code && (
          <>
            <Icon name="arrowRight" size={12} color="var(--ink-faint)" />
            <LangBadge short={tgtL.short} who={who === "you" ? "guest" : "you"} size="sm" />
          </>
        )}
      </div>
      {turn.src_text && (
        <div style={{ fontSize: 15, color: "var(--ink)", lineHeight: 1.45 }}>{turn.src_text}</div>
      )}
      {turn.tgt_text && turn.tgt_text !== turn.src_text && (
        <div style={{ fontSize: 14.5, color: "var(--ink-3)", lineHeight: 1.45, fontStyle: "italic" }}>{turn.tgt_text}</div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
        {turn.has_turn_audio && (
          <PlayChip label="Original" sessionId={sessionId} uttId={uttId} idx={turn.turn_idx} kind="turn" />
        )}
        {turn.has_tts_audio && (
          <PlayChip label="Spoken" sessionId={sessionId} uttId={uttId} idx={turn.turn_idx} kind="tts" />
        )}
      </div>
    </div>
  );
}

function PlayChip({
  label, sessionId, uttId, idx, kind,
}: { label: string; sessionId: number; uttId: string; idx: number; kind: "turn" | "tts" }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  async function toggle() {
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    try {
      if (!urlRef.current) {
        urlRef.current = await api.fetchTurnAudioBlobUrl(sessionId, uttId, idx, kind);
      }
      const a = audioRef.current ?? new Audio();
      audioRef.current = a;
      a.src = urlRef.current;
      a.onended = () => setPlaying(false);
      await a.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  }

  useEffect(() => () => {
    audioRef.current?.pause();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  return (
    <button onClick={toggle} style={{
      display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px 6px 10px",
      background: "var(--surface-2)", color: "var(--ink-2)", borderRadius: "var(--r-pill)",
      border: "1px solid var(--border)", cursor: "pointer", fontFamily: "var(--font-ui)",
      fontSize: 12.5, fontWeight: 600,
    }}>
      <Icon name={playing ? "pause" : "play"} size={14} />
      {label}
    </button>
  );
}

function BackLink({ go }: { go: Go }) {
  return (
    <button onClick={() => go("history")} style={{
      background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-3)",
      display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "var(--font-ui)",
      fontSize: 14, fontWeight: 600, padding: 4,
    }}>
      <Icon name="arrowLeft" size={16} /> Back to history
    </button>
  );
}

function fmtDur(secs: number): string {
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60), s = secs % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

// keep Btn import referenced for tree-shake stability
void Btn;
